import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { A11y, Controller, Grid, Lazy, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { LazyOptions, NavigationOptions } from 'swiper/types';

import { useComponentSettings } from '@context';
import { useMediaQuery, useTheme } from '@hooks';
import { ComponentSectionSliderSettings } from '@schema';

import { focusOutline, media, transition } from '../../styles/mixins';

import { BlogNavigationButton, MainNavigationButton } from './navigation-button';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';
import 'swiper/css/lazy';

export type SliderProps = ComponentSectionSliderSettings & {
	children?: React.ReactElement;
	lazyLoadingOptions?: LazyOptions;
	buttonsType?: string;
};

const defaultSlidesPerView = {
	desktop: 6,
	tablet: 4,
	mobile: 2,
};

const Slider = (props: SliderProps) => {
	const {
		children,
		slidesPerViewDesktop,
		slidesPerViewTablet,
		slidesPerViewMobile,
		buttonsType = 'main',
	} = props;

	const theme = useTheme();
	const [swiperInstance, setSwiperInstance] = useState(null);

	const { slider } = useComponentSettings();
	const isMobile = useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`);
	const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.md}px)`);
	const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg}px)`);
	const slideRect = React.useRef<DOMRect>(null);
	const sliderRef = React.useRef<HTMLDivElement>(null);
	const navigationPrevRef = React.useRef<HTMLButtonElement>(null);
	const navigationNextRef = React.useRef<HTMLButtonElement>(null);

	React.useLayoutEffect(() => {
		if (sliderRef.current) {
			const swiper = sliderRef.current.querySelector('.swiper');
			const swiperWrapper = sliderRef.current.querySelector('.swiper-wrapper');
			// wrap swiperWrapper with div to allow move navigation and pagination to be moved outside
			// the swiper box
			const wrapper = Object.assign(document.createElement('div'), { className: swiper.classList });
			swiperWrapper.parentNode.appendChild(wrapper);
			wrapper.appendChild(swiperWrapper);

			// get slide client rect to set slide default size
			slideRect.current = sliderRef.current.querySelector('.swiper-slide')?.getBoundingClientRect();
		}
	}, [swiperInstance]);

	const isNavigationEnabled = useMemo(() => {
		if (isDesktop) {
			return React.Children.count(children) > (slidesPerViewDesktop || defaultSlidesPerView.mobile);
		} else if (isTablet) {
			return React.Children.count(children) > (slidesPerViewTablet || defaultSlidesPerView.tablet);
		} else if (isMobile) {
			return React.Children.count(children) > (slidesPerViewMobile || defaultSlidesPerView.desktop);
		}

		return false;
	}, [children, slidesPerViewDesktop]);

	// force lazy load after re-render
	useEffect(() => {
		if (swiperInstance) {
			swiperInstance.lazy.load();
		}
	}, [children, slidesPerViewDesktop]);

	return (
		<SliderWrapper
			ref={sliderRef}
			aria-label="carousel"
			data-slide-height={slideRect.current?.height}
		>
			<Swiper
				modules={[Controller, Navigation, Pagination, A11y, Grid, Lazy]}
				spaceBetween={8}
				onSwiper={setSwiperInstance}
				lazy={{
					loadOnTransitionStart: true,
					checkInView: true,
					enabled: props.lazyLoading,
					...(props.lazyLoadingOptions || {}),
				}}
				slidesPerView={slidesPerViewDesktop || defaultSlidesPerView.desktop}
				breakpoints={{
					[theme.breakpoints.sm]: {
						slidesPerView: slidesPerViewMobile || defaultSlidesPerView.mobile,
						slidesPerGroup: slidesPerViewMobile || defaultSlidesPerView.mobile,
					},
					[theme.breakpoints.md]: {
						slidesPerView: slidesPerViewTablet || defaultSlidesPerView.tablet,
						slidesPerGroup: slidesPerViewTablet || defaultSlidesPerView.tablet,
					},
					[theme.breakpoints.lg]: {
						slidesPerView: slidesPerViewDesktop || defaultSlidesPerView.desktop,
						slidesPerGroup: slidesPerViewDesktop || defaultSlidesPerView.desktop,
					},
				}}
				navigation={{
					enabled: isNavigationEnabled,
					prevEl: navigationPrevRef.current,
					nextEl: navigationNextRef.current,
				}}
				onBeforeInit={(swiper) => {
					if (isNavigationEnabled) {
						(swiper.params.navigation as NavigationOptions).prevEl = navigationPrevRef.current;
						(swiper.params.navigation as NavigationOptions).nextEl = navigationNextRef.current;
					}
				}}
				pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 5 }}
			>
				{React.Children.map(children, (child) => (
					<SwiperSlide key={child.key}>{child}</SwiperSlide>
				))}
				{buttonsType === 'main' ? (
					<MainNavigationButton
						isEnabled={isNavigationEnabled}
						prevRef={navigationPrevRef}
						nextRef={navigationNextRef}
						prevRemix={slider.leftButton.remixIcon}
						nextRemix={slider.rightButton.remixIcon}
					/>
				) : (
					<BlogNavigationButton
						isEnabled={isNavigationEnabled}
						prevRef={navigationPrevRef}
						nextRef={navigationNextRef}
						prevRemix={slider.leftButton.remixIcon}
						nextRemix={slider.rightButton.remixIcon}
					/>
				)}
			</Swiper>
		</SliderWrapper>
	);
};

export default Slider;

const SliderWrapper = styled.div<{ 'data-slide-height': number }>`
	--swiper-pagination-bullet-horizontal-gap: 3px;

	grid-area: primary;

	padding-bottom: 32px;

	& > .swiper {
		display: grid;

		min-height: ${(p) => p['data-slide-height']}px;

		overflow: visible;

		& .swiper.swiper-navigation-disabled.swiper-initialized.swiper-horizontal {
			margin-left: initial;
			margin-right: initial;
		}
	}

	.swiper-slide {
		display: flex;
		justify-content: center;
		align-items: center;
		height: auto;
	}

	.swiper-notification {
		display: none;
	}

	.swiper-pagination {
		display: flex;
		justify-content: center;

		height: 11px;
		bottom: -32px !important;
		width: auto !important;

		.swiper-pagination-bullet {
			padding: 0;
			width: 8px;
			height: 8px;

			background: ${(p) => p.theme.colors.transparentWhite};
			border-radius: 26px;

			opacity: 1;

			&:before {
				display: none;
			}

			${transition('width', null, '500ms')};

			&:focus-visible {
				${focusOutline()};
			}

			&[aria-current='true'] {
				width: 24px;

				background: ${(p) => p.theme.gradients.button.hover.primary};
			}
		}
	}

	${media.md`
		--swiper-pagination-bullet-horizontal-gap: 4px;
	`};

	${media.lg`
		padding-bottom: 68px;

		.swiper-pagination {
			gap: 12px;

			bottom: -68px !important;
		}
	`};
`;
